"use client"

import { motion } from "framer-motion"
import { Mail, Phone, MapPin, Clock, Send, User, MessageSquare } from "lucide-react"
import Navbar from "../components/Landing/Navbar"

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-cyan-100">
      {/* Header */}
      <Navbar/>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-cyan-800 mb-4"
          >
            Contact Us
          </motion.h1>
          <p className="text-lg text-cyan-700 mb-6 max-w-2xl mx-auto">
            Have questions about our services? Get in touch with our tax experts today.
          </p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-sm p-8"
          >
            <h2 className="text-2xl font-bold text-cyan-800 mb-6">Send us a message</h2>
            
            <form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-cyan-700 mb-1">
                  Your Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-cyan-400" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    className="block w-full pl-10 pr-3 py-2 border border-cyan-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-cyan-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-cyan-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    className="block w-full pl-10 pr-3 py-2 border border-cyan-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-cyan-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-cyan-400" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    className="block w-full pl-10 pr-3 py-2 border border-cyan-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-cyan-700 mb-1">
                  Your Message
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                    <MessageSquare className="h-5 w-5 text-cyan-400" />
                  </div>
                  <textarea
                    id="message"
                    rows={4}
                    className="block w-full pl-10 pr-3 py-2 border border-cyan-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <Send className="h-5 w-5 mr-2" />
                Send Message
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-cyan-800 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Phone className="h-5 w-5 text-cyan-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-cyan-800">Call Us</h3>
                    <p className="text-cyan-700">+91 1800 123 4567</p>
                    <p className="text-sm text-cyan-600 mt-1">
                      <Clock className="inline h-4 w-4 mr-1" />
                      Mon-Sat, 9:00 AM - 7:00 PM
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-cyan-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-cyan-800">Email Us</h3>
                    <p className="text-cyan-700">support@taxease.com</p>
                    <p className="text-cyan-700">sales@taxease.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-cyan-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-cyan-800">Our Offices</h3>
                    <p className="text-cyan-700">
                      TaxEase Headquarters<br />
                      123 Business Park, Andheri East<br />
                      Mumbai, Maharashtra 400069
                    </p>
                    <p className="text-cyan-700 mt-2">
                      TaxEase Delhi Office<br />
                      Cyber City, Tower B<br />
                      Gurugram, Haryana 122002
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-cyan-800 mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                <div className="border-b border-cyan-100 pb-4">
                  <h3 className="text-lg font-medium text-cyan-800">What documents do I need for ITR filing?</h3>
                  <p className="text-cyan-700 mt-1">
                    Typically you'll need Form 16, bank statements, investment proofs, and rent receipts if applicable.
                  </p>
                </div>

                <div className="border-b border-cyan-100 pb-4">
                  <h3 className="text-lg font-medium text-cyan-800">How long does it take to file my return?</h3>
                  <p className="text-cyan-700 mt-1">
                    Most returns are processed within 48 hours after we receive all required documents.
                  </p>
                </div>

                <div className="border-b border-cyan-100 pb-4">
                  <h3 className="text-lg font-medium text-cyan-800">Do you provide audit support?</h3>
                  <p className="text-cyan-700 mt-1">
                    Yes, we offer complete audit representation and documentation support for all our clients.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 px-4 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-cyan-800 mb-6 text-center">Our Locations</h2>
          
          <div className="bg-cyan-100 rounded-xl overflow-hidden h-96">
            {/* Replace with actual map component */}
            <div className="w-full h-full flex items-center justify-center text-cyan-700">
              <MapPin className="h-12 w-12" />
              <p className="ml-2 text-lg">Interactive Map Would Appear Here</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/90 border-t border-cyan-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 bg-cyan-600 rounded-lg">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-cyan-800">TaxEase</span>
          </div>
          <p className="text-cyan-700 mb-4 text-sm">
            Making tax compliance simple, accurate, and accessible for everyone.
          </p>
          <p className="text-xs text-cyan-600">
            © 2024 TaxEase. All rights reserved. | Licensed by CBDT
          </p>
        </div>
      </footer>
    </div>
  )
}